import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';

const MESSAGE_MAX = 500;

interface Props {
  applicationId: string;
  child: string;
  /** Every rejected document — the email lists all of them. */
  documents: Array<{ key: string; label: string; reason: string }>;
  to: string[];
  onClose: () => void;
  onSent: () => Promise<void> | void;
}

/**
 * One email asking the family to resend every rejected document, each with
 * the reason it was rejected. Staff see exactly what goes and to whom.
 */
const ResendDocumentsDialog: React.FC<Props> = ({ applicationId, child, documents, to, onClose, onSent }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const n = documents.length;

  const send = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.requestDocumentResubmission(applicationId, message.trim());
      enqueueSnackbar(`Email sent to ${to.join(', ')}`, { variant: 'success' });
      await onSent();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t send the email', { variant: 'error' });
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Ask to resend documents</Typography>
        <Typography variant="body2" color="text.secondary">
          One email about {child}’s application, listing {n === 1 ? 'the rejected document' : `all ${n} rejected documents`} with your reasons.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ width: 28, flexShrink: 0 }}>To</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{to.join(', ')}</Typography>
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
              {n === 1 ? 'Document to resend' : `${n} documents to resend`}
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mt: 0.5 }}>
              {documents.map((d, i) => (
                <Box key={d.key} sx={{ px: 1.75, py: 1.25, borderTop: i ? 1 : 0, borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={600}>{d.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{d.reason || 'No reason given'}</Typography>
                </Box>
              ))}
            </Paper>
          </Box>

          <TextField
            label="Add a message (optional)"
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
            placeholder="e.g. Clear phone photos in good light are fine."
            multiline
            minRows={2}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText={`In the email, under the opening line · ${message.length} / ${MESSAGE_MAX}`}
          />
          <Typography variant="caption" color="text.secondary">
            New copies can be uploaded from the application page. Emails can’t be unsent.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={send} disabled={busy}>
          {busy ? 'Sending…' : 'Send email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResendDocumentsDialog;
